/**
 * Serviço para integração do FluxSigner com React
 * Comunica com a extensão Chrome via Native Messaging
 */

// Tipos para as respostas da API
export interface CertificateInfo {
  alias: string;
  commonName: string;
  providerName: string;
  certificateType: string;
  hasPrivateKey: boolean;
  expired: boolean;
  temporallyValid: boolean;
  icpBrasilCompliant: boolean;
  validityStatus: string;
  daysUntilExpiry: number;
  source: string;
  storeLocation: string;
  requiresPin: boolean;
  tokenProvider?: string;
  tokenLabel?: string;
}

export interface CertificateListResponse {
  certificates: CertificateInfo[];
  keystoreStats: string;
  totalFound: number;
  validCount: number;
  expiredCount: number;
  invalidCount: number;
}

export interface SignPDFResponse {
  signedPdfData: string;
  signatureInfo: {
    certificateAlias: string;
    signatureTime: string;
    hasTimestamp: boolean;
    timestampInfo?: {
      timestamp: string;
      tsaUrl: string;
    };
  };
}

export interface SignatureInfo {
  signatureNumber: number;
  integrityValid: boolean;
  certificateInfo: string;
  hasTimestamp: boolean;
  timestampValidationResult?: {
    timestamp: string;
    valid: boolean;
  };
  icpValidationResult?: {
    valid: boolean;
    icpbrasilCertificate: boolean;
    temporalValidity: boolean;
    chainValidity: boolean;
    revocationStatus: string;
  };
}

export interface VerifyPDFResponse {
  signatureCount: number;
  signatures: SignatureInfo[];
  documentInfo: {
    hasSignatures: boolean;
    isModified: boolean;
  };
}

export interface FluxSignerResponse {
  status: "SUCCESS" | "ERROR";
  message: string;
  data?: any;
}

export interface SignPDFOptions {
  reason?: string;
  location?: string;
  useTimestamp?: boolean;
}

// Declaração global do Chrome para TypeScript
declare global {
  interface Window {
    chrome: {
      runtime: {
        sendMessage: (extensionId: string, message: any, callback?: (response: any) => void) => void;
        lastError?: { message: string };
      };
    };
  }
}

class FluxSignerService {
  private static instance: FluxSignerService;
  private extensionId: string = "gmioenipelgmngkedlphkofnbkjdfigk"; // ID oficial da Chrome Web Store
  private isExtensionAvailable: boolean = false;
  private detectedExtensionId: string | null = null;
  private availabilityCheckPromise: Promise<boolean> | null = null;

  private constructor() {
    // O construtor é privado para evitar instanciação direta (Singleton)
  }

  /**
   * Obtém a instância única do serviço (Singleton)
   */
  public static getInstance(): FluxSignerService {
    if (!FluxSignerService.instance) {
      FluxSignerService.instance = new FluxSignerService();
    }
    return FluxSignerService.instance;
  }

  /**
   * Verifica se a extensão está disponível (com cache de promessa)
   */
  async checkExtensionAvailability(): Promise<boolean> {
    if (this.availabilityCheckPromise) {
      return this.availabilityCheckPromise;
    }

    this.availabilityCheckPromise = (async () => {
      try {
        if (typeof window !== "undefined" && window.chrome?.runtime) {
          const isInstalled = await this.isExtensionInstalled();
          if (isInstalled) {
            this.isExtensionAvailable = true;
            console.log("✅ FluxSigner Extension disponível");
            return true;
          }
        }
      } catch (error) {
        this.isExtensionAvailable = false;
        console.warn("⚠️ FluxSigner Extension não disponível:", error);
      }
      this.isExtensionAvailable = false;
      return false;
    })();

    return this.availabilityCheckPromise;
  }

  /**
   * Verifica se a extensão está instalada
   */
  private async isExtensionInstalled(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!window.chrome?.runtime) {
        resolve(false);
        return;
      }

      // Primeiro, tentar com o ID conhecido
      this.tryExtensionId(this.extensionId).then((success) => {
        if (success) {
          this.detectedExtensionId = this.extensionId;
          resolve(true);
          return;
        }

        // Se não funcionar, tentar detectar automaticamente
        this.detectExtensionId().then((detectedId) => {
          if (detectedId) {
            this.detectedExtensionId = detectedId;
            this.extensionId = detectedId;
            console.log("✅ Extensão detectada com ID:", detectedId);
            resolve(true);
          } else {
            resolve(false);
          }
        });
      });
    });
  }

  /**
   * Tenta usar um ID específico de extensão
   */
  private async tryExtensionId(extensionId: string): Promise<boolean> {
    return new Promise((resolve) => {
      window.chrome.runtime.sendMessage(extensionId, { action: "PING" }, (response) => {
        if (window.chrome.runtime.lastError) {
          resolve(false);
        } else {
          resolve(true);
        }
      });
    });
  }

  /**
   * Detecta automaticamente o ID da extensão FluxSigner
   */
  private async detectExtensionId(): Promise<string | null> {
    return new Promise((resolve) => {
      // Lista de IDs possíveis (produção e desenvolvimento)
      const possibleIds = [
        "gmioenipelgmngkedlphkofnbkjdfigk", // ID oficial da Chrome Web Store (produção)
        "hnlmoeeepnmddepiepbcdajfenoimdme", // ID de desenvolvimento local
      ];

      let currentIndex = 0;

      const tryNext = () => {
        if (currentIndex >= possibleIds.length) {
          resolve(null);
          return;
        }

        const id = possibleIds[currentIndex];
        this.tryExtensionId(id).then((success) => {
          if (success) {
            resolve(id);
          } else {
            currentIndex++;
            tryNext();
          }
        });
      };

      tryNext();
    });
  }

  /**
   * Envia mensagem para a extensão
   */
  private async sendMessage(message: any): Promise<FluxSignerResponse> {
    if (!this.isExtensionAvailable) {
      throw new Error("FluxSigner Extension não está disponível. Por favor, instale a extensão no Chrome.");
    }

    const extensionId = this.detectedExtensionId || this.extensionId;

    return new Promise((resolve, reject) => {
      if (!window.chrome?.runtime) {
        reject(new Error("Chrome runtime não disponível"));
        return;
      }

      window.chrome.runtime.sendMessage(extensionId, message, (response) => {
        if (window.chrome.runtime.lastError) {
          reject(new Error(window.chrome.runtime.lastError.message));
        } else if (response && response.status === "SUCCESS") {
          resolve(response);
        } else {
          reject(new Error(response?.message || "Erro desconhecido"));
        }
      });
    });
  }

  /**
   * Lista certificados disponíveis
   */
  async getCertificates(): Promise<CertificateListResponse> {
    const response = await this.sendMessage({
      action: "GET_CERTIFICATES",
      requestId: `cert_${Date.now()}`,
    });

    return JSON.parse(response.message);
  }

  /**
   * Assina um PDF usando base64
   */
  async signPDFBase64(pdfBase64: string, certificateAlias: string, options: SignPDFOptions = {}): Promise<SignPDFResponse> {
    const pureBase64 = pdfBase64.startsWith("data:") ? pdfBase64.split(",")[1] : pdfBase64;
    const response = await this.sendMessage({
      action: "SIGN_PDF",
      requestId: `sign_${Date.now()}`,
      data: {
        content: pureBase64,
        certificateAlias: certificateAlias,
        reason: options.reason || "Assinatura digital via FluxMed",
        location: options.location || "Brasil",
        useTimestamp: options.useTimestamp || false,
      },
    });

    return JSON.parse(response.message);
  }

  /**
   * Verifica assinaturas de um PDF
   */
  async verifyPDFSignatures(pdfBase64: string): Promise<VerifyPDFResponse> {
    const pureBase64 = pdfBase64.startsWith("data:") ? pdfBase64.split(",")[1] : pdfBase64;
    const response = await this.sendMessage({
      action: "VERIFY_PDF_SIGNATURES",
      requestId: `verify_${Date.now()}`,
      data: {
        content: pureBase64,
      },
    });

    return JSON.parse(response.message);
  }

  /**
   * Valida um certificado ICP-Brasil
   */
  async validateICPCertificate(certificateAlias: string): Promise<any> {
    const response = await this.sendMessage({
      action: "VALIDATE_ICP_CERTIFICATE",
      requestId: `validate_${Date.now()}`,
      data: {
        certificateAlias: certificateAlias,
      },
    });

    return JSON.parse(response.message);
  }

  /**
   * Obtém status da extensão
   */
  getExtensionStatus(): { available: boolean; extensionId: string; detectedId?: string } {
    return {
      available: this.isExtensionAvailable,
      extensionId: this.extensionId,
      detectedId: this.detectedExtensionId || undefined,
    };
  }

  /**
   * Força uma nova verificação da extensão
   */
  async forceCheckExtension(): Promise<boolean> {
    this.isExtensionAvailable = false;
    this.detectedExtensionId = null;
    this.availabilityCheckPromise = null; // Reseta a promessa para forçar a re-verificação

    const result = await this.checkExtensionAvailability();
    return result;
  }
}

// Exporta uma instância única do serviço
const fluxSignerService = FluxSignerService.getInstance();
export default fluxSignerService;

